<!-- =========================================================================================
    File Name: AddCompanyModal.vue
    Description: Modernized Add Organization Modal
========================================================================================== -->

<template>
  <div id="page-user-list">
    <!-- Trigger Button -->
    <div class="demo-alignment">
      <vs-button 
        class="primary shadow-lg font-medium px-5 py-3" 
        @click="activePrompt = true" 
        type="filled"
        icon-pack="feather"
        icon="icon-plus"
      >
        {{ $t('addcompany.title') }}
      </vs-button>
    </div>

    <!-- Modern Prompt / Dialog -->
    <vs-prompt
      :title="$t('addcompany.title')"
      :accept-text="$t('Submit')"
      :cancel-text="$t('Cancel')"
      @cancel="clearFields"
      @accept="addCompany"
      @close="clearFields"
      :is-valid="isFormValid"
      :active.sync="activePrompt"
      class="modern-org-modal"
    >
      <form @submit.prevent class="modern-form-wrapper">
        
        <!-- SECTION 1: Organization & Admin Details -->
        <div class="form-section">
          <h6 class="section-title">General Information</h6>
          
          <div class="vx-row">
            <div class="vx-col w-full md:w-1/2 mb-4">
              <label class="field-label">{{ $t('addcompany.lbl0') }} <span class="text-danger">*</span></label>
              <vs-input
                v-validate="'required'"
                name="company_name"
                v-model="company_name"
                class="w-full"
                placeholder="e.g. Acme Corporation"
                icon-pack="feather"
                icon="icon-briefcase"
                icon-no-border
              />
              <span class="text-danger text-xs mt-1 block" v-if="errors.has('company_name')">
                {{ errors.first('company_name') }}
              </span>
            </div>

            <div class="vx-col w-full md:w-1/2 mb-4">
              <label class="field-label">{{ $t('addcompany.lbl1') }} <span class="text-danger">*</span></label>
              <vs-input
                v-validate="'required|email'"
                name="company_email"
                v-model="company_email"
                class="w-full"
                placeholder="admin@organization.com"
                :danger="!validateEmail && company_email !== ''"
                :success="validateEmail"
                type="email"
                icon-pack="feather"
                icon="icon-mail"
                icon-no-border
              />
            </div>
          </div>

          <div class="vx-row">
            <div class="vx-col w-full md:w-1/2 mb-4">
              <label class="field-label">{{ $t('addcompany.lbl2') }} <span class="text-danger">*</span></label>
              <vs-input
                v-validate="'required'"
                name="company_password"
                v-model="company_password"
                class="w-full"
                type="password"
                placeholder="••••••••••••"
                icon-pack="feather"
                icon="icon-lock"
                icon-no-border
              />
            </div>

            <div class="vx-col w-full md:w-1/2 mb-4">
              <label class="field-label">Organization Type</label>
              <v-select
                class="custom-v-select w-full"
                v-model="organization_type"
                label="text"
                :reduce="item => item.value"
                :options="organization_type_list"
                placeholder="Select Type"
              />
            </div>
          </div>
        </div>

        <!-- SECTION 2: Survey & Feedback Intervals -->
        <div class="form-section">
          <h6 class="section-title">Cadence & Survey Frequencies (in days)</h6>
          
          <div class="vx-row">
            <div class="vx-col w-full sm:w-1/3 mb-4">
              <label class="field-label">{{ $t('addcompany.lbl3') }}</label>
              <div class="input-with-badge">
                <vs-input
                  v-validate="'numeric|between:1,99'"
                  name="coaching_survey"
                  v-model="coaching_survey"
                  class="w-full"
                  placeholder="30"
                  type="number"
                />
                <span class="badge-unit">Days</span>
              </div>
            </div>

            <div class="vx-col w-full sm:w-1/3 mb-4">
              <label class="field-label">{{ $t('addcompany.lbl4') }}</label>
              <div class="input-with-badge">
                <vs-input
                  v-validate="'numeric|between:1,99'"
                  name="motivation_survey"
                  v-model="motivation_survey"
                  class="w-full"
                  placeholder="30"
                  type="number"
                />
                <span class="badge-unit">Days</span>
              </div>
            </div>

            <div class="vx-col w-full sm:w-1/3 mb-4">
              <label class="field-label">{{ $t('addcompany.lbl8') }}</label>
              <div class="input-with-badge">
                <vs-input
                  v-validate="'numeric|between:1,99'"
                  name="feedback_frequency"
                  v-model="feedback_frequency"
                  class="w-full"
                  placeholder="15"
                  type="number"
                />
                <span class="badge-unit">Days</span>
              </div>
            </div>
          </div>
        </div>

        <!-- SECTION 3: Settings & Branding -->
        <div class="form-section">
          <h6 class="section-title">Configuration & Appearance</h6>
          
          <div class="vx-row">
            <!-- Terminology Segmented Control -->
            <div class="vx-col w-full md:w-1/2 mb-4">
              <label class="field-label">{{ $t('addcompany.lbl5') }}</label>
              <div class="segmented-control">
                <button
                  type="button"
                  :class="['segment-btn', { active: terminology_type === '0' }]"
                  @click="terminology_type = '0'"
                >
                  COACH
                </button>
                <button
                  type="button"
                  :class="['segment-btn', { active: terminology_type === '1' }]"
                  @click="terminology_type = '1'"
                >
                  DPM
                </button>
                <button
                  type="button"
                  :class="['segment-btn', { active: terminology_type === '2' }]"
                  @click="terminology_type = '2'"
                >
                  SUPER
                </button>
              </div>
            </div>

            <!-- Enterprise Switch -->
            <div class="vx-col w-full md:w-1/2 mb-4 flex items-center">
              <div class="enterprise-toggle-card">
                <div class="toggle-text">
                  <span class="font-medium text-dark text-sm block">{{ $t('addcompany.lbl6') }}</span>
                  <span class="text-muted text-xs">Activate enterprise features</span>
                </div>
                <vs-switch color="success" v-model="enterprise_type" name="enterprise_type">
                  <span slot="on">Yes</span>
                  <span slot="off">No</span>
                </vs-switch>
              </div>
            </div>
          </div>

          <!-- Color Customization -->
          <div class="vx-row mt-2">
            <div class="vx-col w-full md:w-1/2 mb-4">
              <label class="field-label mb-2 block">{{ $t('addcompany.lbl7') }}</label>
              <div class="theme-palette">
                <button
                  type="button"
                  v-for="color in themeColors"
                  :key="color"
                  class="color-swatch"
                  :style="{ backgroundColor: color }"
                  :class="{ active: theme_color === color }"
                  @click="setThemeColor(color)"
                >
                  <i v-if="theme_color === color" class="feather icon-check"></i>
                </button>
              </div>
            </div>

            <div class="vx-col w-full md:w-1/2 mb-4">
              <label class="field-label">{{ $t('addcompany.lbl9') }}</label>
              <div class="flex items-center">
                <vs-input
                  name="secondary_color"
                  v-model="secondary_color"
                  class="w-full"
                  placeholder="#6859B4"
                />
                <span 
                  class="color-preview-box" 
                  :style="{ backgroundColor: secondary_color || '#eee' }"
                ></span>
              </div>
            </div>
          </div>
        </div>

      </form>
    </vs-prompt>
  </div>
</template>

<script>
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { models } from "feathers-vuex";
import MD5 from "crypto-js/md5";
import i18n from "@/i18n/i18n";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "AddCompany",
  components: {
    VuePerfectScrollbar,
    vSelect
  },
  data () {
    return {
      company_email: "",
      company_name: "",
      company_password: "",
      terminology_type: "0",
      enterprise_type: false,
      organization_type: "Business",
      level: false,
      activePrompt: false,
      coaching_survey: "30",
      motivation_survey: "30",
      feedback_frequency: "15",
      theme_color: "#9DCD5B",
      themeColors: [
        "#9DCD5B",
        "#3BA0BE",
        "#6859B4",
        "#E4D033",
        "#EA7526",
        "#C6211B",
        "#F36677",
        "#4A5568"
      ],
      organization_type_list: [
        { value: "Business", text: "Business" },
        { value: "Academic", text: "Academic" },
        { value: "Recruiter", text: "Recruiter" }
      ],
      secondary_color: "#6859B4"
    };
  },
  computed: {
    validateEmail () {
      return (
        !this.errors.has("company_email") &&
        this.company_email.trim() !== "" &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.company_email)
      );
    },
    isFormValid () {
      return (
        this.validateEmail &&
        this.company_name.trim() !== "" &&
        this.company_password.trim() !== ""
      );
    }
  },
  methods: {
    clearFields () {
      this.company_email = "";
      this.company_name = "";
      this.company_password = "";
      this.terminology_type = "0";
      this.enterprise_type = false;
      this.organization_type = "Business";
      this.coaching_survey = "30";
      this.motivation_survey = "30";
      this.feedback_frequency = "15";
    },
    setThemeColor (color) {
      this.theme_color = color;
    },
    addCompany () {
      const company = new models.api.Company({
        email: this.company_email.trim(),
        name: this.company_name.trim(),
        coachingSurveyDays: this.coaching_survey,
        motivationSurveyDays: this.motivation_survey,
        feedbackFrequency: this.feedback_frequency,
        companyPassword: MD5(this.company_password).toString(),
        level: this.level,
        terminology: this.terminology_type,
        enterpriseType: this.enterprise_type,
        organizationType: typeof this.organization_type === "object" ? this.organization_type.value : this.organization_type,
        themeColor: this.theme_color,
        secondaryColor: this.secondary_color,
        status: "active"
      });

      const email = this.company_email.trim();
      if (!email) return;

      const query = { email };

      // Validate existence
      this.$store
        .dispatch("companyManagement/checkExistence", { query })
        .then(({ total }) => {
          if (total) {
            this.$vs.notify({
              title: i18n.t("notification.admin.company.emailExist.success.a1"),
              text: i18n.t("notification.admin.company.emailExist.success.a1"),
              color: "danger"
            });
          } else {
            this.$store
              .dispatch("userManagement/checkExistence", { query })
              .then(({ total }) => {
                if (total) {
                  this.$vs.notify({
                    title: i18n.t("notification.admin.company.emailExist.success.a1"),
                    text: i18n.t("notification.admin.company.emailExist.success.a1"),
                    color: "danger"
                  });
                } else {
                  this.$store
                    .dispatch("companyManagement/addCompany", company)
                    .then(res => {
                      this.$vs.notify({
                        title: i18n.t("notification.admin.company.add.success.a1"),
                        text: i18n.t("notification.admin.company.add.success.a2"),
                        color: "success"
                      });
                      this.clearFields();
                    })
                    .catch(err => {
                      this.$vs.notify({
                        title: i18n.t("notification.admin.company.emailExist.success.e1"),
                        text: i18n.t("notification.admin.company.emailExist.success.e2"),
                        color: "danger"
                      });
                    });
                }
              })
              .catch(err => console.error(err));
          }
        })
        .catch(err => console.error(err));
    }
  }
};
</script>

<style scoped>
/* Modal Container Styling */
.modern-form-wrapper {
  padding: 8px 4px;
}

/* Sections */
.form-section {
  border-bottom: 1px solid #edf2f7;
  padding-bottom: 12px;
  margin-bottom: 16px;
}
.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}
.section-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #718096;
  margin-bottom: 12px;
}

/* Labels */
.field-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 5px;
}

/* Unit Badge Input wrapper */
.input-with-badge {
  position: relative;
  display: flex;
  align-items: center;
}
.badge-unit {
  position: absolute;
  right: 12px;
  top: 9px;
  font-size: 0.75rem;
  color: #a0aec0;
  pointer-events: none;
}

/* Modern Segmented Control */
.segmented-control {
  display: flex;
  background-color: #f1f5f9;
  padding: 4px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}
.segment-btn {
  flex: 1;
  padding: 6px 12px;
  border: none;
  background: transparent;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}
.segment-btn.active {
  background: #ffffff;
  color: rgb(var(--vs-primary));
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}

/* Enterprise Toggle Card */
.enterprise-toggle-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f8fafc;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  width: 100%;
}

/* Color Swatches */
.theme-palette {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.color-swatch {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 1px #cbd5e1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  outline: none;
}
.color-swatch:hover {
  transform: scale(1.1);
}
.color-swatch.active {
  box-shadow: 0 0 0 2px rgb(var(--vs-primary));
  transform: scale(1.15);
}
.color-swatch i {
  color: #ffffff;
  font-size: 14px;
}

/* Secondary Color Preview */
.color-preview-box {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  margin-left: 8px;
  flex-shrink: 0;
}
</style>

<!-- Global override for modal width -->
<style>
.modern-org-modal .vs-dialog {
  max-width: 680px !important;
  border-radius: 12px !important;
  overflow: hidden;
}
.modern-org-modal .vs-dialog-text {
  max-height: 75vh;
  overflow-y: auto;
  padding: 1.25rem 1.75rem !important;
}
.modern-org-modal .vs-dialog-header {
  border-bottom: 1px solid #f1f5f9;
  padding: 1.25rem 1.75rem !important;
}
.modern-org-modal .vs-dialog-footer {
  border-top: 1px solid #f1f5f9;
  padding: 1rem 1.75rem !important;
}
</style>